/**
 * Statistics Overview Page - Main statistics dashboard with visual charts.
 * Provides a comprehensive, at-a-glance view of library metrics.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { get } from "@/api/client";
import {
  BookOpen,
  Users,
  RefreshCw,
  ArrowRight,
  Library,
  Clock,
  AlertTriangle,
  Calendar,
  Wallet,
} from "lucide-react";
import { DonutChart, TrendChart, Gauge, StatTile, ProgressBar } from "../components";

interface DashboardData {
  circulation: {
    activeLoans: number;
    overdueLoans: number;
    loansToday: number;
    returnsToday: number;
    reservationsPending: number;
  };
  collection: {
    totalRecords: number;
    totalItems: number;
    availableItems: number;
    recordsAddedThisMonth: number;
  };
  acquisition: {
    budgetAvailableCents: number;
    budgetUtilizationPercent: number;
    pendingOrders: number;
  };
  patrons: {
    totalPatrons: number;
    activePatrons: number;
    newPatronsThisMonth: number;
  };
  alerts: Array<{
    type: "warning" | "error" | "info";
    category: string;
    message: string;
  }>;
  generatedAt: string;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function StatisticsOverviewPage() {
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ["dashboard"],
    queryFn: () => get<DashboardData>("/statistics/dashboard"),
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-800 dark:bg-red-900/20">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-4 font-medium text-red-800 dark:text-red-200">
            Erreur lors du chargement des statistiques
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Prepare chart data
  const itemStatusData = [
    { name: "Disponibles", value: data.collection.availableItems, color: "#22c55e" },
    {
      name: "En prêt",
      value: data.collection.totalItems - data.collection.availableItems,
      color: "#3b82f6",
    },
  ];

  const patronActivityData = [
    { name: "Actifs", value: data.patrons.activePatrons, color: "#8b5cf6" },
    {
      name: "Inactifs",
      value: data.patrons.totalPatrons - data.patrons.activePatrons,
      color: "#e5e7eb",
    },
  ];

  // Mock trend data (would come from API in real scenario)
  const monthlyTrendData = [
    { name: "Jan", value: 1200 },
    { name: "Fév", value: 1350 },
    { name: "Mar", value: 1180 },
    { name: "Avr", value: 1420 },
    { name: "Mai", value: 1580 },
    { name: "Juin", value: 1320 },
    { name: "Juil", value: 980 },
    { name: "Août", value: 750 },
    { name: "Sept", value: 1450 },
    { name: "Oct", value: 1620 },
  ];

  const overdueRate =
    data.circulation.activeLoans > 0
      ? (data.circulation.overdueLoans / data.circulation.activeLoans) * 100
      : 0;

  const activityRate =
    data.patrons.totalPatrons > 0
      ? (data.patrons.activePatrons / data.patrons.totalPatrons) * 100
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tableau de bord</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Vue d'ensemble de l'activité de la bibliothèque
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <Clock className="h-4 w-4" />
          {new Date(data.generatedAt).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((alert, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 rounded-xl border p-4 ${
                alert.type === "error"
                  ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
                  : alert.type === "warning"
                    ? "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20"
                    : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
              }`}
            >
              <AlertTriangle
                className={`h-5 w-5 ${
                  alert.type === "error"
                    ? "text-red-500"
                    : alert.type === "warning"
                      ? "text-amber-500"
                      : "text-blue-500"
                }`}
              />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {alert.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Key Metrics Row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Notices au catalogue"
          value={data.collection.totalRecords}
          change={data.collection.recordsAddedThisMonth > 0 ? 2.5 : 0}
          changeLabel="ce mois"
          icon={<BookOpen className="h-5 w-5" />}
          color="blue"
          sparklineData={[45, 52, 48, 61, 58, 63, 69, 72, 78, 82]}
        />
        <StatTile
          label="Exemplaires"
          value={data.collection.totalItems}
          icon={<Library className="h-5 w-5" />}
          color="purple"
          sparklineData={[120, 125, 128, 132, 130, 138, 142, 145, 148, 152]}
        />
        <StatTile
          label="Lecteurs actifs"
          value={data.patrons.activePatrons}
          change={data.patrons.newPatronsThisMonth}
          changeLabel="nouveaux"
          icon={<Users className="h-5 w-5" />}
          color="green"
          sparklineData={[280, 295, 310, 305, 320, 335, 340, 352, 360, 375]}
        />
        <StatTile
          label="Prêts en cours"
          value={data.circulation.activeLoans}
          icon={<RefreshCw className="h-5 w-5" />}
          color={data.circulation.overdueLoans > 10 ? "amber" : "blue"}
          sparklineData={[450, 480, 520, 490, 510, 540, 520, 550, 530, 560]}
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Circulation Activity */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 lg:col-span-2 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Activité de prêt
            </h2>
            <Link
              to="/statistics/circulation"
              className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Détails
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <TrendChart data={monthlyTrendData} height={280} color="#3b82f6" />
        </div>

        {/* Item Status Donut */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">État du fonds</h2>
            <Link
              to="/statistics/collections"
              className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Détails
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <DonutChart
            data={itemStatusData}
            centerValue={data.collection.totalItems}
            centerLabel="exemplaires"
            size={200}
          />
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Today's Activity */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Aujourd'hui</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Prêts</span>
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {data.circulation.loansToday}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Retours</span>
              <span className="text-lg font-bold text-green-600 dark:text-green-400">
                {data.circulation.returnsToday}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Réservations</span>
              <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                {data.circulation.reservationsPending}
              </span>
            </div>
          </div>
        </div>

        {/* Overdue Gauge */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-1 flex items-center gap-2">
            <AlertTriangle
              className={`h-5 w-5 ${overdueRate > 10 ? "text-red-500" : "text-amber-500"}`}
            />
            <h3 className="font-semibold text-gray-900 dark:text-white">Taux de retard</h3>
          </div>
          <div className="flex items-center justify-center py-2">
            <Gauge value={overdueRate} size={100} thresholds={{ warning: 5, danger: 10 }} />
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            {data.circulation.overdueLoans} prêts en retard
          </p>
        </div>

        {/* Patron Activity */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Lecteurs</h3>
          </div>
          <div className="mb-3">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {activityRate.toFixed(0)}%
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">taux d'activité</div>
          </div>
          <ProgressBar
            value={data.patrons.activePatrons}
            max={data.patrons.totalPatrons}
            color="#8b5cf6"
            showLabel={false}
          />
          <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{data.patrons.activePatrons.toLocaleString("fr-FR")} actifs</span>
            <span>{data.patrons.totalPatrons.toLocaleString("fr-FR")} total</span>
          </div>
        </div>

        {/* Budget */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-green-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Budget</h3>
          </div>
          <div className="mb-3">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(data.acquisition.budgetAvailableCents)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">disponible</div>
          </div>
          <ProgressBar
            value={data.acquisition.budgetUtilizationPercent}
            max={100}
            thresholds={{ warning: 70, danger: 90 }}
          />
          <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
            {data.acquisition.pendingOrders} commandes en cours
          </p>
        </div>
      </div>

      {/* Bottom Section - Collections & Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Collection Growth */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Croissance du fonds
            </h2>
            <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
              +{data.collection.recordsAddedThisMonth} ce mois
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {data.collection.totalRecords.toLocaleString("fr-FR")}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Notices</div>
            </div>
            <div className="rounded-lg bg-purple-50 p-4 dark:bg-purple-900/20">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {data.collection.totalItems.toLocaleString("fr-FR")}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Exemplaires</div>
            </div>
            <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {data.collection.availableItems.toLocaleString("fr-FR")}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Disponibles</div>
            </div>
            <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-900/20">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {(data.collection.totalItems - data.collection.availableItems).toLocaleString(
                  "fr-FR"
                )}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">En circulation</div>
            </div>
          </div>
        </div>

        {/* Patron Distribution */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Activité des lecteurs
          </h2>
          <div className="flex items-center justify-center">
            <DonutChart
              data={patronActivityData}
              centerValue={`${activityRate.toFixed(0)}%`}
              centerLabel="actifs"
              size={180}
            />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {data.patrons.totalPatrons.toLocaleString("fr-FR")}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
            </div>
            <div>
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                {data.patrons.activePatrons.toLocaleString("fr-FR")}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Actifs</div>
            </div>
            <div>
              <div className="text-xl font-bold text-green-600 dark:text-green-400">
                +{data.patrons.newPatronsThisMonth}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Nouveaux</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/statistics/circulation"
          className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-blue-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-600"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white dark:bg-blue-900/30">
            <RefreshCw className="h-6 w-6" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">Circulation</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Prêts et retours</div>
          </div>
        </Link>
        <Link
          to="/statistics/collections"
          className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-green-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-green-600"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100 text-green-600 transition-colors group-hover:bg-green-600 group-hover:text-white dark:bg-green-900/30">
            <Library className="h-6 w-6" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">Collections</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Fonds documentaire</div>
          </div>
        </Link>
        <Link
          to="/circulation/patrons"
          className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-purple-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-purple-600"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100 text-purple-600 transition-colors group-hover:bg-purple-600 group-hover:text-white dark:bg-purple-900/30">
            <Users className="h-6 w-6" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">Lecteurs</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Gestion des usagers</div>
          </div>
        </Link>
        <Link
          to="/catalog"
          className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-amber-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-amber-600"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600 transition-colors group-hover:bg-amber-600 group-hover:text-white dark:bg-amber-900/30">
            <BookOpen className="h-6 w-6" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">Catalogue</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Recherche</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
